// DomainVerification.tsx
import React, { useState, useRef, useEffect } from 'react';
import { getIsEnterTimeoutActive } from './Chat';

interface DomainVerificationProps {
  onVerificationComplete?: (domain: string, publicKey: string) => void;
  isChatListening: boolean;
  // New prop to communicate modal's open state to parent
  onModalStateChange: (isOpen: boolean) => void;
}

export default function DomainVerification({ onVerificationComplete, isChatListening, onModalStateChange }: DomainVerificationProps) {
  const [isVisible, setIsVisible] = useState(isChatListening);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false); // New state for closing animation
  const [domain, setDomain] = useState('');
  const [challengeMessage, setChallengeMessage] = useState('');
  const [signature, setSignature] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationResult, setVerificationResult] = useState<{
    success: boolean;
    message: string;
    details?: string;
  } | null>(null);
  const [step, setStep] = useState<'input' | 'challenge' | 'signature' | 'verify'>('input');
  const [fetchedPublicKey, setFetchedPublicKey] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  // New state to hold the previously verified domain
  const [previouslyVerifiedDomain, setPreviouslyVerifiedDomain] = useState<string | null>(null);
  const [copyFeedback, setCopyFeedback] = useState(false);

  useEffect(() => {
    setIsVisible(isChatListening);
    if (!isChatListening && isModalOpen) {
      // If chat listening becomes false, ensure the modal is closed and inform parent
      closeModal();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isChatListening]);

  // Effect to load previously verified domain from localStorage on component mount
  useEffect(() => {
    const storedDomain = localStorage.getItem('verifiedDomain');
    if (storedDomain) {
      setPreviouslyVerifiedDomain(storedDomain);
    }
  }, []);

  // Effect to inform parent when modal state changes
  useEffect(() => {
    onModalStateChange(isModalOpen);
  }, [isModalOpen, onModalStateChange]);

  // Effect to apply background blur animation to .container
  useEffect(() => {
    const pageContent = document.querySelector('.container');
    if (pageContent) {
      if (isModalOpen && !isClosing) {
        pageContent.classList.remove('bu-content-out');
        pageContent.classList.add('bu-content-active');
      } else if (isClosing) {
        pageContent.classList.remove('bu-content-active');
        pageContent.classList.add('bu-content-out');
      }
    }
  }, [isModalOpen, isClosing]);

  const validateDomain = (domain: string): boolean => {
    const domainRegex = /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    return domainRegex.test(domain);
  };

  const validatePGPKey = (key: string): boolean => {
    return key.includes('-----BEGIN PGP PUBLIC KEY BLOCK-----') &&
           key.includes('-----END PGP PUBLIC KEY BLOCK-----');
  };

  const validatePGPSignature = (signature: string): boolean => {
    return signature.includes('-----BEGIN PGP SIGNATURE-----') &&
           signature.includes('-----END PGP SIGNATURE-----');
  };

  const normalizeKey = (key: string): string => {
    return key.trim().replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  };

  const extractPGPKeyFromHTML = (html: string): string | null => {
    const linkRegex = /<link\s+[^>]*rel=["']pgpkey["'][^>]*href=["']([^"']+)["'][^>]*>/i;
    const match = html.match(linkRegex);
    return match ? match[1] : null;
  };

  const fetchKeyFromUrl = async (url: string): Promise<string> => {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to fetch key: ${response.status} ${response.statusText}`);
      }
      const keyContent = await response.text();

      if (!validatePGPKey(keyContent)) {
        throw new Error('Invalid PGP key format in the fetched file');
      }

      return keyContent;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      throw new Error(`Failed to fetch PGP key from ${url}: ${errorMessage}`);
    }
  };

  const generateChallengeMessage = (): string => {
    const timestamp = new Date().toISOString();
    return `Domain verification challenge for ${domain} at ${timestamp}`;
  };

  const fetchDomainKey = async () => {
    if (!domain) {
      setVerificationResult({
        success: false,
        message: 'Please provide a domain'
      });
      return;
    }

    if (!validateDomain(domain)) {
      setVerificationResult({
        success: false,
        message: 'Invalid domain format'
      });
      return;
    }

    setIsVerifying(true);
    setVerificationResult(null);

    try {
      const domainUrl = `https://${domain}`;
      let htmlResponse;

      try {
        htmlResponse = await fetch(domainUrl);
        if (!htmlResponse.ok) {
          throw new Error(`Failed to fetch domain page: ${htmlResponse.status}`);
        }
      } catch (error) {
        try {
          const wwwUrl = `https://www.${domain}`;
          htmlResponse = await fetch(wwwUrl);
          if (!htmlResponse.ok) {
            throw new Error(`Failed to fetch domain page (tried both ${domainUrl} and ${wwwUrl})`);
          }
        } catch (wwwError) {
          const primaryError = error instanceof Error ? error.message : 'Unknown error';
          throw new Error(`Cannot access domain: ${primaryError}`);
        }
      }

      const htmlContent = await htmlResponse.text();

      const pgpKeyUrl = extractPGPKeyFromHTML(htmlContent);

      if (!pgpKeyUrl) {
        setVerificationResult({
          success: false,
          message: 'No PGP key link found in domain HTML',
          details: 'Please add a <link rel="pgpkey" href="key.asc"> tag to your website\'s <head> section (href can point to key.pub, key.gpg, or key.asc).'
        });
        return;
      }

      let absoluteKeyUrl = pgpKeyUrl;
      if (pgpKeyUrl.startsWith('/')) {
        absoluteKeyUrl = `https://${domain}${pgpKeyUrl}`;
      } else if (pgpKeyUrl.startsWith('./')) {
        absoluteKeyUrl = `https://${domain}/${pgpKeyUrl.substring(2)}`;
      } else if (!pgpKeyUrl.startsWith('http')) {
        absoluteKeyUrl = `https://${domain}/${pgpKeyUrl}`;
      }

      const fetchedKey = await fetchKeyFromUrl(absoluteKeyUrl);
      setFetchedPublicKey(fetchedKey);

      const challenge = generateChallengeMessage();
      setChallengeMessage(challenge);

      setVerificationResult({
        success: true,
        message: 'PGP key found successfully!',
        details: `Key fetched from: ${absoluteKeyUrl}. Please sign the challenge message below.`
      });
      setStep('challenge');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred during key fetch.';
      setVerificationResult({
        success: false,
        message: 'Failed to fetch PGP key',
        details: errorMessage
      });
    } finally {
      setIsVerifying(false);
    }
  };

  const verifySignature = async () => {
    if (!signature) {
      setVerificationResult({
        success: false,
        message: 'Please provide a signature'
      });
      return;
    }

    if (!validatePGPSignature(signature)) {
      setVerificationResult({
        success: false,
        message: 'Invalid PGP signature format'
      });
      return;
    }

    setIsVerifying(true);
    setVerificationResult(null);

    try {
      const isValidSignature = await verifyPGPSignature(
        challengeMessage,
        signature,
        fetchedPublicKey
      );

      if (isValidSignature) {
        setVerificationResult({
          success: true,
          message: 'Domain ownership verified successfully!',
          details: 'The signature is valid and matches the public key found on your domain.'
        });
        setStep('verify');
        onVerificationComplete?.(domain, fetchedPublicKey);
        localStorage.setItem('verifiedDomain', domain); // Store the successfully verified domain
        setPreviouslyVerifiedDomain(domain); // Update state as well
      } else {
        setVerificationResult({
          success: false,
          message: 'Signature verification failed',
          details: 'The provided signature does not match the challenge message or the public key found on your domain.'
        });
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred during signature verification.';
      setVerificationResult({
        success: false,
        message: 'Verification failed',
        details: errorMessage
      });
    } finally {
      setIsVerifying(false);
    }
  };

  const loadOpenPGP = async (): Promise<any> => {
    return new Promise((resolve, reject) => {
      if ((window as any).openpgp) {
        resolve((window as any).openpgp);
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://unpkg.com/openpgp@5.11.0/dist/openpgp.min.js';
      script.onload = () => {
        resolve((window as any).openpgp);
      };
      script.onerror = () => {
        reject(new Error('Failed to load OpenPGP.js'));
      };
      document.head.appendChild(script);
    });
  };

  const verifyPGPSignature = async (
    message: string,
    signature: string,
    publicKey: string
  ): Promise<boolean> => {
    try {
      const openpgp = await loadOpenPGP();

      const publicKeyObj = await openpgp.readKey({ armoredKey: publicKey });

      let verificationResult;

      if (signature.includes('-----BEGIN PGP SIGNED MESSAGE-----')) {
        const clearsignedMessage = await openpgp.readCleartextMessage({
          cleartextMessage: signature
        });

        verificationResult = await openpgp.verify({
          message: clearsignedMessage,
          verificationKeys: publicKeyObj
        });

        const signedText = clearsignedMessage.getText();
        if (signedText.trim() !== message.trim()) {
          console.error('Signed message does not match challenge message');
          console.log('Expected:', message.trim());
          console.log('Actual:', signedText.trim());
          return false;
        }
      } else {
        const detachedSignature = await openpgp.readSignature({
          armoredSignature: signature
        });

        const messageObj = await openpgp.createMessage({ text: message });

        verificationResult = await openpgp.verify({
          message: messageObj,
          signature: detachedSignature,
          verificationKeys: publicKeyObj
        });
      }

      const { signatures } = verificationResult;
      for (const sig of signatures) {
        try {
          await sig.verified;
          return true;
        } catch (e) {
          console.error('Signature verification failed:', e);
        }
      }

      return false;
    } catch (error) {
      console.error('PGP verification error:', error);
      throw new Error(`PGP verification failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        setSignature(content);
      };
      reader.readAsText(file);
    }
  };

  const resetForm = () => {
    setDomain('');
    setChallengeMessage('');
    setSignature('');
    setFetchedPublicKey('');
    setVerificationResult(null);
    setStep('input');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const closeModal = () => {
    setIsClosing(true);
    // Timeout matches the 500ms duration of the blowUpModalTwo animation
    setTimeout(() => {
      setIsModalOpen(false);
      setIsClosing(false);
      resetForm();
    }, 500);
  };

  const openModal = () => {
    setIsModalOpen(true);
  };

  const goBack = () => {
    if (step === 'verify') {
      setStep('signature');
    } else if (step === 'signature') {
      setStep('challenge');
    } else if (step === 'challenge') {
      setStep('input');
    }
    setVerificationResult(null);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopyFeedback(true);
    setTimeout(() => setCopyFeedback(false), 2000); // Hide after 2 seconds
  };

  if (!isChatListening && !getIsEnterTimeoutActive()) return null;

  return (
    <>
      {/* Verification speech bubble */}
      <div
        className="bubble"
        onClick={openModal}
        style={{
          position: 'fixed',
          bottom: '95px', // Position above the cursor preview
          right: '23px',
          zIndex: 10001,
          textAlign: 'center',
          lineHeight: '1.2',
          margin: '0',
          backgroundColor: '#fff',
          border: '1.5px solid #666',
          fontFamily: 'sans-serif',
          fontSize: '16px',
          fontWeight: 500,
          width: '50px',
          borderRadius: '30px',
          padding: '6px 12px 2px 12px',
          color: '#666',
          cursor: 'pointer',
        }}
      >
        get
        <img
          src="data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxNCIgaGVpZ2h0PSIxNCIgdmlld0JveD0iMCAwIDIwIDIwIj4KCTxwYXRoIGZpbGw9IiMzMzMiIGZpbGwtcnVsZT0iZXZlbm9kZCIgZD0iTTYuMjY3IDMuNDU1YTMuMDcgMy4wNyAwIDAgMCAxLjc0NS0uNzIzYTMuMDY2IDMuMDY2IDAgMCAxIDMuOTc2IDBhMy4wNyAzLjA3IDAgMCAwIDEuNzQ1LjcyM2EzLjA2NiAzLjA2NiAwIDAgMSAyLjgxMiAyLjgxMmMuMDUxLjY0My4zMDQgMS4yNTQuNzIzIDEuNzQ1YTMuMDY2IDMuMDY2IDAgMCAxIDAgMy45NzZhMy4wNyAzLjA3IDAgMCAwLS43MjMgMS43NDVhMy4wNjYgMy4wNjYgMCAwIDEtMi44MTIgMi44MTJhMy4wNyAzLjA3IDAgMCAwLTEuNzQ1LjcyM2EzLjA2NiAzLjA2NiAwIDAgMS0zLjk3NiAwYTMuMDcgMy4wNyAwIDAgMC0xLjc0NS0uNzIzYTMuMDY2IDMuMDY2IDAgMCAxLTIuODEyLTIuODEyYTMuMDcgMy4wNyAwIDAgMC0uNzIzLTEuNzQ1YTMuMDY2IDMuMDY2IDAgMCAxIDAtMy45NzZhMy4wNyAzLjA3IDAgMCAwIC43MjMtMS43NDVhMy4wNjYgMy4wNjYgMCAwIDEgMi44MTItMi44MTJtNy40NCA1LjI1MmExIDEgMCAwIDAtMS40MTQtMS40MTRMOSAxMC41ODZMNy43MDcgOS4yOTNhMSAxIDAgMCAwLTEuNDE0IDEuNDE0bDIgMmExIDEgMCAwIDAgMS40MTQgMHoiIGNsaXAtcnVsZT0iZXZlbm9kZCIgLz4KPC9zdmc+"
          alt="Verified"
          style={{
            width: "15px",
            height: "15px",
            display: "inline-block",
            verticalAlign: "middle"
          }}
        />

        <style>{`
          div.bubble:before,
          div.bubble:after {
            content: ' ';
            position: absolute;
            width: 0;
            height: 0;
          }
          div.bubble:before,
          div.bubble:after {
            left: 9px;
            bottom: -3px;
            width: 7px;
            height: 7px;
            background-color: #fff;
            border:1.5px solid #666;
            -webkit-border-radius: 28px;
            -moz-border-radius: 28px;
            border-radius: 32px;
          }
          div.bubble:after {
            width: 4px;
            height: 4px;
            left: 8px;
            bottom: -7px;
            -webkit-border-radius: 18px;
            -moz-border-radius: 18px;
            border-radius: 18px;
          }
        `}</style>
      </div>

      {/* Verification Modal */}
      {(isModalOpen || isClosing) && (
        <>
          <style>{`
            @keyframes blowUpModal { 0% { transform: scale(0); } 100% { transform: scale(1); } }
            @keyframes blowUpModalTwo { 0% { transform: scale(1); opacity: 1; } 100% { transform: scale(0); opacity: 0; } }
            @keyframes blowUpContent { 0% { transform: scale(1); opacity: 1; } 100% { opacity: 0.7; filter: blur(2px); } }
            @keyframes blowUpContentTwo { 0% { opacity: 0.7; filter: blur(2px); } 100% { transform: scale(1); opacity: 1; filter: blur(0px); } }
            .bu-content-active { animation: blowUpContent 0.5s cubic-bezier(0.165, 0.840, 0.440, 1.000) forwards; }
            .bu-content-out { animation: blowUpContentTwo 0.5s cubic-bezier(0.165, 0.840, 0.440, 1.000) forwards; }
          `}</style>
          
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 20000,
              backdropFilter: 'blur(4px)',
            }}
            onClick={closeModal} // Closes modal if clicking on the background, matching room.js's feel
          >
            <div
              style={{
                background: 'white',
                borderRadius: '12px',
                padding: '24px',
                width: '90%',
                maxWidth: '600px',
                maxHeight: '85vh',
                overflow: 'auto',
                fontFamily: 'system-ui, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                
                // Added blowup animations
                transform: 'translateZ(0)',
                animation: isClosing
                  ? 'blowUpModalTwo 0.5s cubic-bezier(0.165, 0.840, 0.440, 1.000) forwards'
                  : 'blowUpModal 0.5s cubic-bezier(0.165, 0.840, 0.440, 1.000) forwards',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#666"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ marginRight: '8px' }}
                >
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="2" y1="12" x2="22" y2="12"/>
                  <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                </svg>
                <h2 style={{ margin: 0, color: '#333', fontSize: '18px' }}>
                  Domain Ownership Verification
                </h2>
              </div>

              {/* Display previously verified domain message */}
              {previouslyVerifiedDomain && (step === 'input' || step === 'verify') && (
                <div style={{
                  marginBottom: '16px',
                  padding: '12px',
                  background: '#d4edda',
                  borderRadius: '6px',
                  fontSize: '14px',
                  color: '#155724',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="20,6 9,17 4,12"/>
                  </svg>
                  <span>You've already verified as <strong>"{previouslyVerifiedDomain}"</strong><br/>It'll show as username once you type</span>
                </div>
              )}

              {/* Step 1: Input domain */}
              {step === 'input' && (
                <>
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#333' }}>
                      Domain
                    </label>
                    <input
                      type="text"
                      value={domain}
                      onChange={(e) => setDomain(e.target.value)}
                      placeholder="example.com"
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        fontSize: '14px',
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>

                  <div style={{ marginBottom: '16px', padding: '12px', background: '#e3f2fd', borderRadius: '6px', fontSize: '13px', color: '#1976d2' }}>
                    <strong>How it works:</strong>
                    <ol style={{ margin: '8px 0 0 0', paddingLeft: '16px' }}>
                      <li>Upload your PGP public key file to your domain (e.g., key.asc, key.pub, key.gpg)<br/>💡 here's an <a href="https://dev.to/adityabhuyan/how-to-generate-your-own-public-and-secret-keys-for-pgp-encryption-1joh" target='_blank'>guide to generating a PGP key</a></li>
                      <li>Add this HTML tag to your website's &lt;head&gt; section:<br />
                        <code style={{ background: '#fff', padding: '4px', borderRadius: '3px', display: 'inline-block', marginTop: '4px' }}>
                          &lt;link rel="pgpkey" href="key.asc"&gt;
                        </code>
                      </li>
                      <li>Enter your domain above and click "Fetch Key"</li>
                      <li>Sign the generated challenge message with your private key</li>
                      <li>Submit the signature to verify domain ownership</li>
                    </ol>
                  </div>

                  <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                    <button
                      onClick={closeModal}
                      style={{
                        padding: '10px 20px',
                        background: '#f0f0f0',
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px',
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={fetchDomainKey}
                      disabled={!domain || isVerifying}
                      style={{
                        padding: '10px 20px',
                        background: !domain || isVerifying ? '#ccc' : '#007bff',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: !domain || isVerifying ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                      }}
                    >
                      {isVerifying ? 'Fetching Key...' : 'Fetch Key'}
                    </button>
                  </div>
                </>
              )}

              {/* Step 2: Challenge message */}
              {step === 'challenge' && (
                <>
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#333' }}>
                      Challenge Message
                    </label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <span style={{ fontSize: '14px', color: '#666' }}>
                        Copy this message and sign it with your private key:
                      </span>
                      <button
                        onClick={() => copyToClipboard(challengeMessage)}
                        style={{
                          padding: '4px 8px',
                          background: copyFeedback ? '#d4edda' : '#f0f0f0',
                          color: copyFeedback ? '#155724' : 'inherit',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '12px',
                        }}
                      >
                        {copyFeedback ? 'Copied!' : 'Copy'}
                      </button>
                    </div>
                    <textarea
                      value={challengeMessage}
                      readOnly
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        fontSize: '14px',
                        boxSizing: 'border-box',
                        minHeight: '70px',
                        fontFamily: 'monospace',
                        background: '#f9f9f9',
                      }}
                    />
                  </div>

                  <div style={{ marginBottom: '16px', padding: '12px', background: '#fff3cd', borderRadius: '6px', fontSize: '13px', color: '#856404' }}>
                    <strong>Run this command in terminal:</strong>
                    <code style={{ background: '#fff', padding: '4px', borderRadius: '3px', display: 'inline-block', marginTop: '4px' }}>echo 'put challenge message here' | gpg --clearsign</code>
                  </div>

                  <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                    <button
                      onClick={goBack}
                      style={{
                        padding: '10px 20px',
                        background: '#f0f0f0',
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px',
                      }}
                    >
                      Back
                    </button>
                    <button
                      onClick={() => setStep('signature')}
                      style={{
                        padding: '10px 20px',
                        background: '#007bff',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px',
                      }}
                    >
                      Next
                    </button>
                  </div>
                </>
              )}

              {/* Step 3: Signature input */}
              {step === 'signature' && (
                <>
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#333' }}>
                      PGP Signature
                    </label>
                    <textarea
                      value={signature}
                      onChange={(e) => setSignature(e.target.value)}
                      placeholder="-----BEGIN PGP SIGNATURE-----"
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        fontSize: '14px',
                        boxSizing: 'border-box',
                        minHeight: '120px',
                        fontFamily: 'monospace',
                      }}
                    />
                  </div>

                  <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                    <button
                      onClick={goBack}
                      style={{
                        padding: '10px 20px',
                        background: '#f0f0f0',
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px',
                      }}
                    >
                      Back
                    </button>
                    <button
                      onClick={verifySignature}
                      disabled={!signature || isVerifying}
                      style={{
                        padding: '10px 20px',
                        background: !signature || isVerifying ? '#ccc' : '#28a745',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: !signature || isVerifying ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                      }}
                    >
                      {isVerifying ? 'Verifying...' : 'Verify Signature'}
                    </button>
                  </div>
                </>
              )}

              {/* Verification Result */}
              {verificationResult && step !== 'signature' && (
                <div
                  style={{
                    padding: '12px',
                    borderRadius: '6px',
                    marginBottom: '16px',
                    background: verificationResult.success ? '#d4edda' : '#f8d7da',
                    border: `1px solid ${verificationResult.success ? '#c3e6cb' : '#f5c6cb'}`,
                    color: verificationResult.success ? '#155724' : '#721c24',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    {verificationResult.success ? (
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="20,6 9,17 4,12"/>
                      </svg>
                    ) : (
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <line x1="18" y1="6" x2="6" y2="18"/>
                        <line x1="6" y1="6" x2="18" y2="18"/>
                      </svg>
                    )}
                    <span style={{ fontSize: '14px', fontWeight: '500' }}>{verificationResult.message}</span>
                  </div>
                  {verificationResult.details && (
                    <div style={{ fontSize: '13px', marginLeft: '24px' }}>
                      {verificationResult.details}
                    </div>
                  )}
                </div>
              )}

              {/* Success state with option to verify another domain */}
              {verificationResult?.success && step === 'verify' && (
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                  <button
                    onClick={closeModal}
                    style={{
                      padding: '10px 20px',
                      background: '#28a745',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px',
                    }}
                  >
                    Done
                  </button>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
}